import React, { cloneElement, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import { useEffectOnce } from 'react-use';
import useToggle from 'react-use/lib/useToggle';
import { formatText } from '../../../utils/format/formatText';
import { IconHelp } from '../../icon/IconHelp';
import { Box } from '../../layout/Box';
import { Flex } from '../../layout/Flex';
import { Typography } from '../../typography/Typography';
import { AUTO_OPEN_TIMEOUT } from './constants';
import { Arrow } from './parts/Arrow';
import { TooltipProps, WithHintProps } from './types';

export const Tooltip = (props: TooltipProps) => {
    const {
        trigger,
        options = { placement: 'bottom-start' },
        children,
        isVisible,
        onClose,
        variant = 'default',
        disableHoverEvents,
        offset,
        stopPropagation,
        autoOpen,
    } = props;

    const [open, toggleOpen] = useToggle(Boolean(isVisible));
    const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
    const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
    const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);

    const { styles, attributes, update, state } = usePopper(referenceElement, popperElement, {
        ...options,
        modifiers: [
            {
                name: 'arrow',
                enabled: true,
                options: {
                    element: arrowElement,
                },
            },
            ...(offset
                ? [
                      {
                          name: 'offset',
                          options: {
                              offset,
                          },
                      },
                  ]
                : []),
        ],
    });

    const handleToggle = (value: boolean) => {
        if (value) {
            onClose?.();
        }

        toggleOpen(value);
        update?.();
    };

    useEffectOnce(() => {
        if (autoOpen) {
            setTimeout(() => {
                toggleOpen(true);
                update?.();
            }, AUTO_OPEN_TIMEOUT);
        }
    });

    useEffectOnce(() => {
        const handleClick = () => {
            autoOpen && handleToggle(false);
            onClose?.();
        };

        const onCloseByScroll = () => {
            handleToggle(false);
        };

        document.addEventListener('click', handleClick);
        document.addEventListener('scroll', onCloseByScroll, true);

        return () => {
            document.removeEventListener('click', handleClick);
            document.removeEventListener('scroll', onCloseByScroll, true);
        };
    });

    return (
        <>
            {cloneElement(trigger, {
                ref: setReferenceElement,
                onMouseOver: disableHoverEvents
                    ? undefined
                    : () => {
                          handleToggle(true);
                      },
                onMouseOut: disableHoverEvents
                    ? undefined
                    : () => {
                          handleToggle(false);
                      },
            })}
            {open
                ? createPortal(
                      <Box
                          onClick={(e) => {
                              stopPropagation && e.stopPropagation();

                              onClose?.();
                          }}
                          ref={setPopperElement}
                          zIndex={9999}
                          cursor="default"
                          style={styles.popper}
                          {...attributes.popper}
                      >
                          <Box
                              style={styles.offset}
                              display={open ? 'block' : 'none'}
                              my={1}
                              mx={1}
                              maxWidth={300}
                              position="relative"
                              background="var(--background-invert)"
                              px={2}
                              py={1}
                              borderRadius={2}
                          >
                              <Arrow
                                  state={state}
                                  variant={variant}
                                  setArrowElement={setArrowElement}
                                  popperElement={popperElement}
                              />
                              {children}
                          </Box>
                      </Box>,
                      document.body,
                  )
                : null}
        </>
    );
};

export const WithHint = ({
    message,
    children,
    iconColor,
    trigger,
    stopPropagation,
    ...other
}: WithHintProps) => {
    const handleHintClick = (e: React.MouseEvent) => {
        stopPropagation && e.stopPropagation();
    };

    return (
        <Flex
            inline
            direction="row"
            alignItems="center"
            spacing={0.5}
            onClick={handleHintClick}
            {...other}
        >
            {children}
            <Tooltip
                trigger={
                    trigger ?? (
                        <IconHelp
                            color={iconColor ?? 'var(--color-primary)'}
                            cursor="help"
                            size={16}
                        />
                    )
                }
            >
                <Typography variant="caption" color="var(--color-invert)">
                    {formatText(message)}
                </Typography>
            </Tooltip>
        </Flex>
    );
};
